import { redeemCreditCode } from '@/api/credits';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { CreditPackages } from '@/components/settings/credits/credit-packages';
import { CreditsTable } from '@/components/settings/credits/credits-table';
import { DataTableSkeleton } from '@/components/data-table/data-table-skeleton';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreditHistory, useInvalidateCredits } from '@/hooks/use-credits';
import { m } from '@/locale/paraglide/messages';
import { Routes } from '@/lib/routes';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  IconArrowRight,
  IconCoins,
  IconGift,
  IconLoader2,
  IconReceipt2,
  IconSparkles,
} from '@tabler/icons-react';
import { useState } from 'react';

export const Route = createFileRoute('/settings/credits')({
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
  }),
  component: CreditsPage,
});

function CreditsPage() {
  const { returnTo } = Route.useSearch();
  const breadcrumbs = [
    { label: m.settings_credits_title(), isCurrentPage: true },
  ];

  return (
    <DashboardLayout
      breadcrumbs={breadcrumbs}
      title={m.settings_credits_title()}
      description={m.settings_credits_description()}
    >
      <div className="grid gap-6">
        <CreditOverviewSection />
        <CreditPackages returnUrl={returnTo} />
        <TransactionHistorySection />
      </div>
    </DashboardLayout>
  );
}

function asNumber(value: unknown) {
  return typeof value === 'number' ? value : 0;
}

function CreditOverviewSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <CardTitle className="text-sm font-medium">
            {m.settings_credits_balance_title()}
          </CardTitle>
          <IconCoins className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-20" />
          <Skeleton className="mt-2 h-4 w-36" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <CardTitle className="text-sm font-medium">
            {m.settings_credits_total_used_title()}
          </CardTitle>
          <IconReceipt2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-20" />
          <Skeleton className="mt-2 h-4 w-36" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <CardTitle className="text-sm font-medium">
            {m.settings_credits_buy_title()}
          </CardTitle>
          <IconSparkles className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-32" />
          <Skeleton className="mt-2 h-4 w-36" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <CardTitle className="text-sm font-medium">
            {m.settings_credits_redeem_title()}
          </CardTitle>
          <IconGift className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="mt-2 flex items-center gap-2">
            <Skeleton className="h-9 flex-1" />
            <Skeleton className="h-9 w-20" />
          </div>
          <Skeleton className="mt-2 h-4 w-52" />
        </CardContent>
      </Card>
    </div>
  );
}

function CreditOverviewSection() {
  const { data, isLoading } = useCreditHistory();

  if (isLoading) {
    return <CreditOverviewSkeleton />;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <CardTitle className="text-sm font-medium">
            {m.settings_credits_balance_title()}
          </CardTitle>
          <IconCoins className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold">{asNumber(data?.balance)}</div>
          <p className="mt-2 text-muted-foreground text-xs">
            {m.settings_credits_balance_description()}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <CardTitle className="text-sm font-medium">
            {m.settings_credits_total_used_title()}
          </CardTitle>
          <IconReceipt2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold">{asNumber(data?.totalUsed)}</div>
          <p className="mt-2 text-muted-foreground text-xs">
            {m.settings_credits_total_used_description()}
          </p>
        </CardContent>
      </Card>
      <RedeemCodeCard />
      <BuyCreditsCard />
    </div>
  );
}

function BuyCreditsCard() {
  return (
    <Link
      to={Routes.Pricing}
      className="group group/card flex flex-col gap-4 rounded-xl border border-amber-300 bg-amber-50 py-4 transition-colors hover:border-amber-400 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:hover:border-amber-400/60 dark:hover:bg-amber-500/20"
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
        <CardTitle className="font-medium text-amber-700 text-sm dark:text-amber-300">
          {m.settings_credits_buy_title()}
        </CardTitle>
        <IconSparkles className="h-4 w-4 text-amber-500 dark:text-amber-400" />
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <div className="font-bold text-amber-800 text-2xl dark:text-amber-200">
          {m.settings_credits_buy_bonus()}
        </div>
        <p className="mt-1 text-amber-700/80 text-xs dark:text-amber-300/70">
          {m.settings_credits_buy_description()}
        </p>
        <span className="mt-auto inline-flex items-center gap-1 self-end pt-2 font-medium text-amber-700 text-xs group-hover:underline dark:text-amber-300">
          {m.settings_credits_buy_view_plans()}
          <IconArrowRight className="h-3 w-3" />
        </span>
      </CardContent>
    </Link>
  );
}

function RedeemCodeCard() {
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const invalidateCredits = useInvalidateCredits();

  const redeemMutation = useMutation({
    mutationFn: (code: string) => redeemCreditCode({ data: { code } }),
    onSuccess: (result) => {
      if (result.success) {
        setMessage({
          type: 'success',
          text: result.message ?? m.settings_credits_redeem_success(),
        });
        setCode('');
        invalidateCredits();
      } else {
        setMessage({
          type: 'error',
          text: result.error ?? m.settings_credits_redeem_error(),
        });
      }
    },
    onError: (error) => {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : m.common_unknown_error(),
      });
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
        <CardTitle className="text-sm font-medium">
          {m.settings_credits_redeem_title()}
        </CardTitle>
        <IconGift className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim()) {
              setMessage(null);
              redeemMutation.mutate(code.trim());
            }
          }}
          className="mt-2 flex items-center gap-2"
        >
          <Input
            placeholder={m.settings_credits_redeem_placeholder()}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="max-w-[50%] uppercase"
          />
          <Button
            type="submit"
            className="shrink-0"
            disabled={!code.trim() || redeemMutation.isPending}
          >
            {redeemMutation.isPending ? (
              <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {m.settings_credits_redeem_button()}
          </Button>
        </form>
        {message ? (
          <p
            className={`mt-2 text-xs ${
              message.type === 'success'
                ? 'text-green-600 dark:text-green-400'
                : 'text-destructive'
            }`}
          >
            {message.text}
          </p>
        ) : (
          <p className="mt-2 text-muted-foreground text-xs">
            {m.settings_credits_redeem_hint()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function TransactionHistorySection() {
  const { data, isLoading } = useCreditHistory();
  const transactions = data?.transactions ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.settings_credits_history_title()}</CardTitle>
        <CardDescription>
          {m.settings_credits_history_description()}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <DataTableSkeleton
            columnCount={5}
            rowCount={5}
            filterCount={2}
            withPagination={true}
          />
        ) : (
          <CreditsTable data={transactions} />
        )}
      </CardContent>
    </Card>
  );
}
